// ---------------------------------------------------------------------------
// First-party attribution capture. Runs on every client entry regardless of
// the analytics consent choice — it is a first-party cookie only, nothing is
// sent anywhere. Stored on .duffloo.com so app.duffloo.com can read it too.
// Shape: { ft: <first touch, write-once>, lt: <last touch, overwritten only
// when a new visit carries a utm/click-id or an external referrer> }.
//
// A second, separate cookie `_dfl_acq_ref` carries the referral code alone.
// It is not merged into `_dfl_attr` because it is not this site's cookie to
// define — the customer app already reads and writes it — and because it obeys
// a different rule: first touch only, never overwritten. See below.
// ---------------------------------------------------------------------------

const ATTR_COOKIE = "_dfl_attr";
const ATTR_COOKIE_MAX_AGE = 7776000; // 90 days
const MAX_LANDING_PAGE_LENGTH = 500;

const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
];
const CLICK_ID_KEYS = ["gclid", "gbraid", "wbraid", "fbclid", "ttclid", "msclkid"];

/**
 * Referral capture.
 *
 * `_dfl_acq_ref` is NOT this file's invention — it is the cookie the customer
 * app's web build already writes and reads
 * (DufflooCustomerApp/src/global/helpers/acquisitionCapture.web.js), on
 * `.duffloo.com` so both hosts share it. Every convention below is that file's:
 * the `referral_code` URL parameter, the 90-day max-age, SameSite=Lax, the raw
 * code URI-encoded as the value, and first-touch-wins.
 *
 * Until now this site captured utm_* and six click ids and dropped
 * `referral_code` on the floor, so a referral link that landed on
 * www.duffloo.com — the marketing site, which is where a shared link actually
 * goes — credited nobody. app.duffloo.com captured it; duffloo.com did not.
 *
 * The one deliberate difference: the domain attribute comes from
 * attrCookieDomain() rather than being hard-coded, so the cookie also works on
 * localhost and on Netlify previews. On duffloo.com the two writers produce a
 * byte-identical cookie.
 */
const REFERRAL_COOKIE = "_dfl_acq_ref";
const REFERRAL_PARAM = "referral_code";

const attrCookieDomain = () =>
  window.location.hostname.endsWith("duffloo.com") ? "; domain=.duffloo.com" : "";

const readAttrCookie = () => {
  try {
    const row = document.cookie
      .split("; ")
      .find((entry) => entry.startsWith(`${ATTR_COOKIE}=`));
    if (!row) {
      return null;
    }
    return JSON.parse(decodeURIComponent(row.slice(ATTR_COOKIE.length + 1)));
  } catch {
    return null;
  }
};

const writeAttrCookie = (value) => {
  document.cookie = `${ATTR_COOKIE}=${encodeURIComponent(
    JSON.stringify(value)
  )}${attrCookieDomain()}; path=/; max-age=${ATTR_COOKIE_MAX_AGE}; SameSite=Lax`;
};

const readReferralCookie = () => {
  try {
    const row = document.cookie
      .split("; ")
      .find((entry) => entry.startsWith(`${REFERRAL_COOKIE}=`));
    return row ? decodeURIComponent(row.slice(REFERRAL_COOKIE.length + 1)) : null;
  } catch {
    return null;
  }
};

const writeReferralCookie = (code) => {
  document.cookie = `${REFERRAL_COOKIE}=${encodeURIComponent(
    code
  )}${attrCookieDomain()}; path=/; max-age=${ATTR_COOKIE_MAX_AGE}; SameSite=Lax`;
};

const isExternalReferrer = () => {
  if (!document.referrer) {
    return false;
  }
  try {
    const host = new URL(document.referrer).hostname;
    return host !== window.location.hostname && !host.endsWith("duffloo.com");
  } catch {
    return false;
  }
};

const buildTouch = () => {
  const params = new URLSearchParams(window.location.search);
  const touch = {};
  [...UTM_KEYS, ...CLICK_ID_KEYS, REFERRAL_PARAM].forEach((key) => {
    const value = params.get(key);
    if (value) {
      touch[key] = value;
    }
  });
  touch.landing_page = `${window.location.pathname}${window.location.search}`.slice(
    0,
    MAX_LANDING_PAGE_LENGTH
  );
  if (document.referrer) {
    touch.referrer = document.referrer;
  }
  touch.at = new Date().toISOString();
  return touch;
};

export const captureAttribution = () => {
  if (typeof window === "undefined") {
    return;
  }

  try {
    const touch = buildTouch();
    // Arriving on a referral link is a new acquisition touch in its own right,
    // so it refreshes last-touch exactly as a utm or a click id does.
    const hasCampaignSignal = [...UTM_KEYS, ...CLICK_ID_KEYS, REFERRAL_PARAM].some(
      (key) => touch[key]
    );
    const stored = readAttrCookie() || {};
    const next = { ...stored };
    if (!next.ft) {
      next.ft = touch; // first touch is write-once
    }
    if (!next.lt || hasCampaignSignal || isExternalReferrer()) {
      next.lt = touch;
    }
    writeAttrCookie(next);

    // The referral code is FIRST touch only, in its own shared cookie, and is
    // never overwritten: whoever's link brought this person to Duffloo is the
    // referrer, and a later campaign click must not take the credit away from
    // them. Same rule and same cookie as the customer app's
    // captureAcquisitionFromInitialLink().
    if (touch[REFERRAL_PARAM] && !readReferralCookie()) {
      writeReferralCookie(touch[REFERRAL_PARAM]);
    }
  } catch {
    // Attribution must never break the page.
  }
};

/**
 * The captured referral code, or null.
 *
 * Reads the shared cookie first and falls back to the current URL, so a page
 * that renders before onClientEntry has run (or on a host where the cookie
 * write failed) still sees the code on the link it was opened with.
 */
export const getReferralCode = () => {
  if (typeof window === "undefined") {
    return null;
  }
  try {
    const stored = readReferralCookie();
    if (stored) {
      return stored;
    }
    return new URLSearchParams(window.location.search).get(REFERRAL_PARAM) || null;
  } catch {
    return null;
  }
};

// Flattened acquisition snapshot for persisting on account/washer records.
// Returns null when nothing has been captured (so callers can spread-omit it).
export const getAcquisition = () => {
  if (typeof window === "undefined") {
    return null;
  }

  const stored = readAttrCookie();
  if (!stored) {
    return null;
  }

  const first = stored.ft || {};
  const last = stored.lt || first;
  const acquisition = {
    // camelCase deliberately, alone among these keys. `referralCode` is the
    // name the native/customer-app dialect already uses and the only name this
    // field has anywhere in the codebase (bigqueryExport.job.js documents both
    // dialects and names it `referralCode`). Writing `referral_code` here would
    // invent a third spelling for one field.
    referralCode: getReferralCode() || first[REFERRAL_PARAM] || last[REFERRAL_PARAM],
    source: last.utm_source,
    medium: last.utm_medium,
    campaign: last.utm_campaign,
    term: last.utm_term,
    content: last.utm_content,
    gclid: last.gclid,
    fbclid: last.fbclid,
    ttclid: last.ttclid,
    landing_page: last.landing_page,
    referrer: last.referrer,
    first_touch_at: first.at,
    last_touch_at: last.at,
  };
  Object.keys(acquisition).forEach((key) => {
    if (!acquisition[key]) {
      delete acquisition[key];
    }
  });
  return Object.keys(acquisition).length > 0 ? acquisition : null;
};
